/**
 * O tema da tela: claro, escuro, ou o do sistema operacional.
 *
 * Os tokens já têm os dois temas (`shared/estilo/tokens.css`): sem
 * `data-theme` no `<html>`, quem decide é o `prefers-color-scheme`; com ele, a
 * escolha da pessoa ganha. Aqui só se guarda a escolha, neste navegador, e se
 * carimba o atributo. É uma preferência de quem usa o aparelho, e não da
 * organização: não vai ao servidor.
 */

import { useEffect, useState } from 'react';

export type Tema = 'sistema' | 'claro' | 'escuro';

const CHAVE = 'cliqcrm:tema';
const ESCURO_DO_SISTEMA = '(prefers-color-scheme: dark)';

/** A escolha guardada; sem escolha (ou sem como ler), a do sistema. */
export function lerTema(deposito: Storage | undefined = globalThis.localStorage): Tema {
  try {
    const lido = deposito?.getItem(CHAVE);
    return lido === 'claro' || lido === 'escuro' ? lido : 'sistema';
  } catch {
    return 'sistema';
  }
}

/** Carimba o tema no `<html>`: o do sistema tira o atributo e deixa a mídia decidir. */
export function aplicarTema(tema: Tema, raiz: HTMLElement = document.documentElement): void {
  if (tema === 'sistema') raiz.removeAttribute('data-theme');
  else raiz.setAttribute('data-theme', tema === 'escuro' ? 'dark' : 'light');
}

function gravarTema(tema: Tema, deposito: Storage | undefined = globalThis.localStorage): void {
  try {
    if (tema === 'sistema') deposito?.removeItem(CHAVE);
    else deposito?.setItem(CHAVE, tema);
  } catch {
    // Navegação anônima de alguns navegadores: o tema vale até fechar a aba.
  }
}

function sistemaEscuro(): boolean {
  return globalThis.matchMedia?.(ESCURO_DO_SISTEMA).matches ?? false;
}

/**
 * O tema que está valendo (`escuro`, quando a escolha é do sistema e ele está
 * escuro) e o jeito de trocar. Trocar grava, carimba e vale na hora.
 */
export function useTema(): { escuro: boolean; alternar: () => void } {
  const [tema, definirTema] = useState<Tema>(() => lerTema());
  const [doSistema, definirDoSistema] = useState(sistemaEscuro);

  // Com a escolha do sistema, a tela acompanha quando o sistema muda.
  useEffect(() => {
    const midia = globalThis.matchMedia?.(ESCURO_DO_SISTEMA);
    if (!midia) return undefined;
    const mudou = (): void => definirDoSistema(midia.matches);
    midia.addEventListener('change', mudou);
    return () => midia.removeEventListener('change', mudou);
  }, []);

  const escuro = tema === 'sistema' ? doSistema : tema === 'escuro';
  const alternar = (): void => {
    const proximo: Tema = escuro ? 'claro' : 'escuro';
    gravarTema(proximo);
    aplicarTema(proximo);
    definirTema(proximo);
  };
  return { escuro, alternar };
}
